import React, { useCallback, useEffect, useState, useRef } from "react";
import AssetList from "./AssetList";
import "./AssetsBar.css";
import { buildApiUrl } from '../../config/api';

const AssetsBar = ({ assets, setAssets, onSelectAsset }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const dragCounter = useRef(0);

  // Get siteId from URL
  const siteId = window.location.pathname.split('/editor/')[1];

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getOriginalFileName = (fileName) => {
    const dashIndex = fileName.indexOf("-");
    return dashIndex !== -1 ? fileName.substring(dashIndex + 1) : fileName;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = useCallback(
    async (event) => {
      event.preventDefault();
      dragCounter.current = 0;
      setIsDraggingOver(false);
      const files = event.dataTransfer.files;

      if (files.length > 0) {
        try {
          setIsUploading(true);

          const getDimensions = (file) => {
            return new Promise((resolve) => {
              const img = new Image();
              img.src = URL.createObjectURL(file);
              img.onload = () => {
                URL.revokeObjectURL(img.src);
                resolve({
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                });
              };
            });
          };

          const dimensions = await getDimensions(files[0]);

          const formData = new FormData();
          formData.append("file", files[0]);
          formData.append("fileSize", files[0].size);
          formData.append("width", dimensions.width);
          formData.append("height", dimensions.height);

          const response = await fetch(
            buildApiUrl(`api/sites/${siteId}/assets`),
            {
              method: "POST",
              body: formData,
            }
          );

          const data = await response.json();

          // Show success checkmark
          const loadingSpinner = document.querySelector(".loading-spinner");
          const successMark = document.querySelector(".loading-success");
          if (loadingSpinner && successMark) {
            loadingSpinner.style.display = "none";
            successMark.classList.remove("hidden");
            successMark.classList.add("visible");
          }

          // Wait a moment to show the success mark
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Refresh assets list
          const assetsResponse = await fetch(
            buildApiUrl(`api/sites/${siteId}/assets`)
          );
          const assetsData = await assetsResponse.json();

          // Sort assets by uploadedAt in descending order (newest first)
          const sortedAssets = assetsData.assets.sort(
            (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
          );

          setAssets(sortedAssets);
        } catch (error) {
          console.error("Error uploading file:", error);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [setAssets, siteId]
  );

  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      try {
        setIsUploading(true);
        // Use your existing upload logic here
        // ... upload code ...
      } catch (error) {
        console.error("Error uploading file:", error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch = asset.fileName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "image" &&
        asset.type === "image" &&
        (asset.mimeType?.includes("image") || asset.type === "image")) ||
      (filter === "lottie" && asset.type === "lottie");
    return matchesSearch && matchesFilter;
  });

  return (
    <div
      className="assets-sidebar"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDraggingOver ? (
        <div className="full-height-drop-zone">
          <div className="drop-zone-content">
            <p>Drop your files here</p>
            <span className="drop-zone__prompt">📁</span>
          </div>
        </div>
      ) : (
        <>
          <div className="assets-header">
            <h3>Assets</h3>
            <div className="assets-controls">
              <select
                className="filter-dropdown"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Assets</option>
                <option value="image">Images</option>
                <option value="lottie">Lottie</option>
              </select>
              <label className="upload-btn">
                <input
                  type="file"
                  accept={filter === "lottie" ? ".json" : "image/*"}
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
                <span>Upload</span>
              </label>
            </div>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          {filteredAssets.length === 0 && !isUploading ? (
            <div className="no-assets-message">
              <p>No matching assets</p>
            </div>
          ) : (
            <AssetList
              assets={filteredAssets}
              setAssets={setAssets}
              formatFileSize={formatFileSize}
              getOriginalFileName={getOriginalFileName}
              isUploading={isUploading}
              onSelectAsset={onSelectAsset}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AssetsBar;
